import {
  DETAILED_SITE_SUMMARY_KEY,
  SITE_SUMMARY_KEY,
  useInvalidateSiteQueries,
} from "@src/global_functions/sites/sitesQueries";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import FetchClients, { ClientOutput } from "../client";

export type SiteAccountRelationship =
  ClientOutput["siteAccounts"]["SiteAccountRelationship"];

const siteAccounts = {
  useQueryAccountSiteRelationships,
  useQuerySiteAccountRelationships,
  mutations: {
    useUpsertSiteAccount,
    useDeleteSiteAccount,
  },
};

export default siteAccounts;

export const ACCOUNT_SITES_KEY = "accountSites";

async function getAccountSiteRelationships(
  account_id: number
): Promise<SiteAccountRelationship[]> {
  const { data, error } = await FetchClients.siteAccounts.GET(
    "/{account_id}/sites",
    {
      params: { path: { account_id } },
    }
  );
  if (data) return data;
  throw new Error(error);
}

function useQueryAccountSiteRelationships(
  accountId?: number // not enabled if undefined
) {
  return useQuery({
    queryKey: [ACCOUNT_SITES_KEY, accountId],
    queryFn: () => getAccountSiteRelationships(accountId!),
    enabled: typeof accountId === "number",
  });
}

export const SITE_ACCOUNTS_KEY = "siteAccounts";

async function getSiteAccountRelationships(
  site_id: number
): Promise<SiteAccountRelationship[]> {
  const { data, error } = await FetchClients.site.GET(
    "/{site_id}/site-accounts",
    {
      params: { path: { site_id } },
    }
  );
  if (data) return data;
  throw new Error(error);
}

function useQuerySiteAccountRelationships(siteId: number) {
  return useQuery({
    queryKey: [SITE_ACCOUNTS_KEY, siteId],
    queryFn: () => getSiteAccountRelationships(siteId),
    refetchOnMount: false,
    staleTime: Infinity,
  });
}

function useUpsertSiteAccount() {
  const { refreshSiteQueriesAsync } = useInvalidateSiteQueries();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      siteId,
      accountId,
      isAdministratorAccount,
    }: {
      siteId: number;
      accountId: number;
      isAdministratorAccount: boolean;
    }) => {
      const { data, error } = await FetchClients.site.PUT(
        "/{site_id}/site-accounts/{account_id}",
        {
          params: { path: { account_id: accountId, site_id: siteId } },
          body: {
            is_administrator_account: isAdministratorAccount,
          },
        }
      );
      if (data) return data;
      throw new Error(error);
    },
    onSuccess: async (_, { accountId, siteId, isAdministratorAccount }) => {
      await queryClient.invalidateQueries({
        queryKey: [ACCOUNT_SITES_KEY, accountId],
      });
      await queryClient.refetchQueries({
        queryKey: [SITE_ACCOUNTS_KEY, siteId],
      });
      if (isAdministratorAccount) {
        await refreshSiteQueriesAsync();
        await queryClient.invalidateQueries({
          queryKey: [DETAILED_SITE_SUMMARY_KEY, String(siteId)],
        });
        await queryClient.invalidateQueries({ queryKey: [SITE_SUMMARY_KEY] });
      }
    },
  });
}

function useDeleteSiteAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      siteId,
      accountId,
    }: {
      siteId: number;
      accountId: number;
    }) => {
      const { data, error } = await FetchClients.site.DELETE(
        "/{site_id}/site-accounts/{account_id}",
        {
          params: { path: { account_id: accountId, site_id: siteId } },
        }
      );
      if (data) return data;
      throw new Error(error);
    },
    onSuccess: (_, { accountId, siteId }) => {
      queryClient.invalidateQueries({
        queryKey: [ACCOUNT_SITES_KEY, accountId],
      });
      queryClient.invalidateQueries({ queryKey: [SITE_ACCOUNTS_KEY, siteId] });
    },
  });
}
